import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button } from '../components/Button'
import { Tag } from '../components/Tag'
import { api } from '../services/api'
import styles from './IncidentScreen.module.css'

type IncidentStatus = 'new' | 'reviewing' | 'resolved' | 'reopen'

const statusLabels: Record<IncidentStatus, string> = {
  new: 'New',
  reviewing: 'Reviewing',
  resolved: 'Resolved',
  reopen: 'Reopen',
}

export function IncidentScreen() {
  const { eventId } = useParams<{ eventId: string }>()
  const navigate = useNavigate()
  const [status, _setStatus] = useState<IncidentStatus>('new')
  const [type, setType] = useState('')
  const [description, setDescription] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  const handleSubmit = async () => {
    if (type && description) {
      setActionError(null)
      try {
        await api.incidents.create({ incident_type: type, description, eventId: eventId ? Number(eventId) : undefined })
        setSubmitted(true)
      } catch {
        setActionError('Failed to submit report. Please try again.')
      }
    }
  }

  // Use _setStatus to satisfy TS unused-variable check
  // The status state is used for the tag variants below

  return (
    <div className={styles.container}>
      {actionError && (
        <div className={styles.errorBanner}>
          <span>{actionError}</span>
          <button onClick={() => setActionError(null)} className={styles.errorBannerButton}>×</button>
        </div>
      )}
      <div className={styles.backButtonContainer}>
        <button
          onClick={() => navigate(-1)}
          className={styles.backButton}
        >
          ← Back
        </button>
      </div>

      <div className={styles.header}>
        <h2 className={styles.headerTitle}>Safety</h2>
        <p className={styles.headerSubtitle}>
          Confidential reporting. Your identity is protected.
        </p>

        <div className={styles.statusContainer}>
          <Tag variant={status === 'new' ? 'accent' : 'neutral'}>{statusLabels.new}</Tag>
          <Tag variant={status === 'reviewing' ? 'accent' : 'neutral'}>{statusLabels.reviewing}</Tag>
          <Tag variant={status === 'resolved' ? 'accent' : 'neutral'}>{statusLabels.resolved}</Tag>
        </div>

        {submitted ? (
          <div className={styles.submittedContainer}>
            <Tag variant="accent" className={styles.submittedTag}>Report submitted</Tag>
            <p className={styles.submittedText}>
              A trusted organiser will review your report.
            </p>
          </div>
        ) : (
          <div className={styles.formContainer}>
            <div>
              <label htmlFor="incident-type" className={styles.formLabel}>
                Type
              </label>
              <select
                id="incident-type"
                value={type}
                onChange={e => setType(e.target.value)}
                className={styles.formSelect}
              >
                <option value="">Select type...</option>
                <option value="harassment">Harassment</option>
                <option value="safety_concern">Safety concern</option>
                <option value="code_of_conduct">Code of conduct violation</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label htmlFor="incident-description" className={styles.formLabel}>
                Description
              </label>
              <textarea
                id="incident-description"
                value={description}
                onChange={e => setDescription(e.target.value)}
                rows={4}
                className={styles.formTextarea}
              />
            </div>

            <Button className={`${styles.submitButton} btn-block`} disabled={!type || !description} onClick={handleSubmit}>
              Submit confidential report
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
